import { useEffect, useMemo } from 'react'

import { BillingAddonReferenceFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'
import { getDatoLocale } from 'modules/onboarding/lib/datoLocale'
import useJoinOneWaitlist from 'modules/upsell/hooks/useJoinOneWaitlist'
import { SelfUpsellProvider } from 'modules/upsell/store/SelfUpsellProvider'

// Component that executes inside the provider and exposes joinWaitlist
type WaitlistExecutorProps = {
  availableAddons: BillingAddonReferenceFragment[]
  moduleName: string
  onReady: (joinFn: (() => Promise<unknown>) | null) => void
}

const WaitlistExecutor = ({ availableAddons, moduleName, onReady }: WaitlistExecutorProps) => {
  const billingAddon = useMemo(() => availableAddons[0] || null, [availableAddons])
  const joinWaitlist = useJoinOneWaitlist({ billingAddon, moduleName })

  useEffect(() => {
    if (joinWaitlist) {
      onReady(joinWaitlist)
    }
  }, [joinWaitlist, onReady])

  return null
}

type WaitlistProviderBoundaryProps = {
  onReady: (joinFn: (() => Promise<unknown>) | null) => void
}

export const WaitlistProviderBoundary = ({ onReady }: WaitlistProviderBoundaryProps) => {
  const {
    access: { locale: accessLocale },
  } = useCurrentSession()
  const locale = getDatoLocale(accessLocale)
  const { data: moduleData, loading } = useGetDatoModules({
    locale,
    slug: 'one',
    showHiddenModules: true,
  })

  const availableAddons = useMemo(
    () => moduleData?.at(0)?.billingAddonReference || [],
    [moduleData]
  )
  const moduleName = useMemo(() => moduleData?.at(0)?.chargebeeModuleName || '', [moduleData])

  useEffect(() => {
    if (loading || !availableAddons.length || !moduleName) {
      onReady(null)
    }
  }, [loading, availableAddons.length, moduleName, onReady])

  if (loading || !availableAddons.length || !moduleName) return null

  return (
    <SelfUpsellProvider
      availableAddons={availableAddons}
      availableCrossSellingAddons={[]}
      moduleName={moduleName}
    >
      <WaitlistExecutor
        availableAddons={availableAddons}
        moduleName={moduleName}
        onReady={onReady}
      />
    </SelfUpsellProvider>
  )
}
